/**
 * PreviewViewToolbar.tsx - PreviewViewToolbar - Toolbar for PreviewView
 * 
 * CHANGE HISTORY:
 * - 251021_0641 [a6c7876] danielbeer: docs(inspectr): complete upgrade documentation
 */
import React from 'react';

export interface PreviewViewToolbarProps {
  currentFormat: string;
  formats: string[];
  stats?: {
    itemCount: number;
    size: string;
    bytes: number;
    format: string;
    lines: number;
  };
  enableClipboard: boolean;
  enableDownload: boolean;
  enableSearch: boolean;
  enableDrag: boolean;
  showSearch: boolean;
  searchQuery: string;
  onFormatChange: (format: string) => void;
  onCopy: () => void;
  onDownload: () => void;
  onToggleSearch: () => void;
  onSearchChange: (query: string) => void;
  onDragExport: () => void;
}

const PreviewViewToolbar: React.FC<PreviewViewToolbarProps> = ({
  currentFormat,
  formats,
  stats,
  enableClipboard,
  enableDownload,
  enableSearch,
  enableDrag,
  showSearch,
  searchQuery,
  onFormatChange,
  onCopy,
  onDownload,
  onToggleSearch,
  onSearchChange,
  onDragExport
}) => {
  return (
    <div className="preview-view-toolbar">
      <div className="preview-view-toolbar-left">
        {/* Format Selector */}
        <div className="preview-view-format-selector">
          <label htmlFor="format-select" className="preview-view-label">
            Format:
          </label>
          <select
            id="format-select"
            className="preview-view-select"
            value={currentFormat}
            onChange={(e) => onFormatChange(e.target.value)}
          >
            {formats.map(format => (
              <option key={format} value={format}>
                {format.toUpperCase()}
              </option>
            ))}
          </select>
        </div>

        {/* Statistics */}
        {stats && (
          <div className="preview-view-stats">
            <span className="preview-view-stat">
              <span className="preview-view-stat-label">Items:</span>
              <span className="preview-view-stat-value">{stats.itemCount}</span>
            </span>
            <span className="preview-view-stat-separator">•</span>
            <span className="preview-view-stat">
              <span className="preview-view-stat-label">Size:</span>
              <span className="preview-view-stat-value">{stats.size}</span>
            </span>
            <span className="preview-view-stat-separator">•</span>
            <span className="preview-view-stat">
              <span className="preview-view-stat-label">Lines:</span>
              <span className="preview-view-stat-value">{stats.lines}</span>
            </span>
          </div>
        )}
      </div>

      <div className="preview-view-toolbar-right">
        {/* Action Buttons */}
        <div className="preview-view-actions">
          {enableSearch && (
            <button
              className={`btn btn-ghost btn-sm ${showSearch ? 'active' : ''}`}
              onClick={onToggleSearch}
              title="Search"
            >
              🔍
            </button>
          )}

          {enableClipboard && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={onCopy}
              title="Copy to clipboard"
            >
              📋
            </button>
          )}

          {enableDownload && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={onDownload}
              title="Download"
            >
              💾
            </button>
          )}

          {enableDrag && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={onDragExport}
              title="Drag to export"
              draggable
            >
              🔄
            </button>
          )}
        </div>
      </div>

      {/* Search Input */}
      {showSearch && (
        <div className="preview-view-search">
          <input
            type="text"
            className="preview-view-search-input"
            placeholder="Search in preview..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            autoFocus
          />
          <button
            className="btn btn-ghost btn-sm"
            onClick={onToggleSearch}
            title="Close search"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
};

export default PreviewViewToolbar;
